import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './Badge';
import { Button } from './Button';
import type { FeatureItem, FeatureMaturity } from '@dopomogai/types';
import {
  type LucideIcon,
  DollarSign,
  Clock,
  GitCommit,
  Zap,
  Layers,
  PlugZap,
  Brain,
  BookOpenCheck,
  LineChart,
  MessageSquare,
  Database,
  Settings,
  Shield,
  Building2,
  FileText,
} from 'lucide-react';

const maturityBadgeMap: Record<
  FeatureMaturity,
  { text: string; color: 'green' | 'blue' | 'yellow' | 'red' | 'gray'; icon?: React.ReactNode }
> = {
  shipped_now: { text: 'Shipped', color: 'green', icon: <Zap size={12} /> },
  '1w': { text: '1 Week', color: 'blue' },
  '3w': { text: '3 Weeks', color: 'yellow' },
  research: { text: 'Research', color: 'red' },
};

// Longest-prefix match category -> icon
const categoryIconMap: Record<string, LucideIcon> = {
  'core.setup': Settings,
  'core.security': Shield,
  'core.governance': Building2,
  integrations: PlugZap,
  intelligence: Brain,
  knowledge: BookOpenCheck,
  analytics: LineChart,
  channels: MessageSquare,
  data: Database,
  docs: FileText,
};

function getCategoryIcon(categoryId: string): LucideIcon {
  let bestKey = '';
  for (const key of Object.keys(categoryIconMap)) {
    if (categoryId.startsWith(key) && key.length > bestKey.length) bestKey = key;
  }
  return bestKey ? categoryIconMap[bestKey] : Layers;
}

function formatPrice(price: number): string {
  if (price >= 1000) {
    const thousands = price / 1000;
    return `$${thousands.toFixed(0)}k`;
  }
  return `$${price}`;
}

const ACRONYMS = new Set(['CRM', 'SSO', 'IVR', 'GA4', 'POS', 'QBO']);
function prettifyCategory(category: string): string {
  return category
    .split('.')
    .map((seg) => {
      const cleaned = seg.replace(/_/g, ' ');
      const upper = cleaned.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
    })
    .join(' / ');
}

type FeatureCardProps = {
  feature: FeatureItem;
  index: number;
  onClick?: (feature: FeatureItem) => void;
  className?: string;

  // Optional CTA (kept flexible for other pages)
  ctaText?: string; // e.g., "Get a quote"
  onCtaClick?: (feature: FeatureItem) => void;

  // Backward-compat alias to avoid changing call sites
  onCta?: (feature: FeatureItem) => void;

  showCategoryIcon?: boolean; // defaults to true
};

export const FeatureCard = ({
  feature,
  index,
  onClick,
  className,
  ctaText,
  onCtaClick,
  onCta, // alias for backward compat
  showCategoryIcon = true,
}: FeatureCardProps) => {
  const {
    title,
    category,
    maturity,
    price_from,
    price_to,
    eta_days_from,
    eta_days_to,
    dependencies,
  } = feature;

  const maturityInfo = maturityBadgeMap[maturity];
  const effectiveCta = onCtaClick ?? onCta;
  const depsTitle = dependencies.length ? dependencies.join(', ') : undefined;
  const CategoryIcon = getCategoryIcon(category);

  const isInteractive = Boolean(onClick || effectiveCta);

  const handleRootClick = () => {
    if (onClick) onClick(feature);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(feature);
    }
  };

  const etaBadgeText =
    eta_days_from === eta_days_to
      ? `${eta_days_from}d ETA`
      : `${eta_days_from}-${eta_days_to}d ETA`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: Math.min(index, 10) * 0.05 }}
      whileHover={
        isInteractive
          ? { y: -4, x: -4, boxShadow: '4px 4px 0px 0px rgb(17 24 39)' }
          : undefined
      }
      whileTap={
        isInteractive
          ? { y: 0, x: 0, boxShadow: '1px 1px 0px 0px rgb(17 24 39 / 0.4)', scale: 0.99 }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onClick={onClick ? handleRootClick : undefined}
      aria-label={onClick ? `Open feature ${title}` : undefined}
      style={{ WebkitTapHighlightColor: 'transparent' }}
      className={[
        'bg-white border-2 border-black rounded-xl p-5 flex flex-col h-full',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
        '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-xl',
        '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
        className || '',
      ].join(' ')}
    >
      {/* Header: icon + title + breadcrumb.
          Note: We removed the Tier badge to free space for the title. */}
      <div className="flex items-start gap-3">
        {showCategoryIcon && (
          <div className="w-10 h-10 rounded-lg border-2 border-black bg-slate-100 flex items-center justify-center shrink-0 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-white/10">
            <CategoryIcon size={18} className="text-slate-700 [.theme-liquid-glass_&]:text-white/70" />
          </div>
        )}
        <div className="min-w-0">
          <h3 className="font-extrabold text-slate-900 text-lg leading-tight line-clamp-2 [.theme-liquid-glass_&]:text-white">
            {title}
          </h3>
          <div className="text-sm font-semibold text-slate-500 mt-1 truncate [.theme-liquid-glass_&]:text-white/60">
            {prettifyCategory(category)}
          </div>
        </div>
      </div>

      {/* Facts: price + maturity. ETA moved to bottom next to CTA. */}
      <div className="mt-4 space-y-2 text-sm font-medium text-slate-700 [.theme-liquid-glass_&]:text-white/80">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-slate-400 [.theme-liquid-glass_&]:text-white/50" />
          <span>
            {formatPrice(price_from)} - {formatPrice(price_to)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {maturityInfo.icon || <GitCommit size={14} className="text-slate-400 [.theme-liquid-glass_&]:text-white/50" />}
          <span className="font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/80">{maturityInfo.text}</span>
        </div>
      </div>

      <div className="flex-grow" />

      {/* Bottom: dependencies (left) + ETA badge next to CTA (right) */}
      <div className="mt-4 pt-3 border-t-2 border-slate-100 flex items-center justify-between gap-2 [.theme-liquid-glass_&]:border-t [.theme-liquid-glass_&]:border-white/10">
        {dependencies.length > 0 ? (
          <div className="text-xs text-slate-500 truncate [.theme-liquid-glass_&]:text-white/60" title={depsTitle}>
            <strong>Requires:</strong>{' '}
            {dependencies.length > 2 ? `${dependencies.length} items` : dependencies.join(', ')}
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2">
          <Badge
            text={etaBadgeText}
            icon={<Clock size={12} />}
            accentColor="gray"
          />
          {effectiveCta && (
            <Button
              variant="secondary"
              size="sm"
              text={ctaText || 'Get a quote'}
              onClick={(e) => {
                e.stopPropagation();
                effectiveCta(feature);
              }}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500"
            />
          )}
        </div>
      </div>
    </motion.article>
  );
};
